import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Spacer } from '@Sage/hoi-poi-ui';
import { useSelector } from 'react-redux';
import { getAutomationApiKeys } from 'services/AutomationsService';
import { getSrcUserCircleAvatar } from 'utils/getSrcAvatar';
import { getLiteral } from 'utils/getLiteral';
import { errorToast } from 'utils/toast';
import InformationSection from './components/InformationSection';
import IntegrationsSection from './components/IntegrationsSection';

import './styles.scss';

const buildInitialForm = (data, { userId, userName, userEmail }) => {
    const { src, fallbackSrc } = getSrcUserCircleAvatar(userId);

    const defaultNotificationEmail = [
        {
            type: 3,
            label: userName,
            subLabel: userEmail,
            email: userEmail,
            value: parseInt(userId, 10),
            src,
            placeholder: fallbackSrc,
        },
    ];

    const notificationEmail = data?.notificationEmail?.length
        ? data.notificationEmail.map((item) => {
              const { src, fallbackSrc } = getSrcUserCircleAvatar(userId);
              return { ...item, subLabel: item.email, src, placeholder: fallbackSrc };
          })
        : defaultNotificationEmail;

    return {
        name: data?.name,
        description: data?.description,
        integrations: data?.integrations || [],
        enabledErrorNotification: data?.hasOwnProperty('enabledErrorNotification')
            ? data.enabledErrorNotification
            : true,
        notificationEmail,
    };
};

const AutomationBuilderHeaderModalForm = memo(({ onRef, onSave }) => {
    const saveCb = useRef(null);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState();
    const [errors, setErrors] = useState({});
    const [integrations, setIntegrations] = useState([]);
    const { userEmail, userName, userId } = useSelector((state) => ({
        userEmail: state?.config?.userData?.email,
        userName: `${state?.config?.userData?.nombre} ${state?.config?.userData?.apellidos}`,
        userId: state?.config?.userData?.idUsuario,
    }));

    useEffect(() => {
        getAutomationApiKeys()
            .then((result) => {
                const sorted = result.sort((a, b) => b.connector - a.connector);
                setIntegrations(sorted);
            })
            .catch((e) => {
                console.error(e);
                errorToast({ text: getLiteral('error_generalerror') });
                setIntegrations([]);
            });
    }, []);

    useEffect(() => {
        if (!onRef) return;

        onRef({
            open(data, onSaveCb) {
                if (onSaveCb) saveCb.current = onSaveCb;
                setErrors({});
                setForm(buildInitialForm(data, { userId, userName, userEmail }));
                setOpen(true);
            },
        });
    }, [onRef, userEmail, userId, userName]);

    const handleFormChange = useCallback((field, value) => {
        setForm((current) => ({ ...current, [field]: value }));
    }, []);

    const handleIntegrationsChange = useCallback((newIntegrations) => {
        setForm((current) => ({ ...current, integrations: newIntegrations }));
    }, []);

    const handleCancel = useCallback(() => {
        setOpen(false);
    }, []);

    const handleSave = useCallback(() => {
        setOpen(false);
        if (errors?.name || errors?.description) return;

        const formToSave = { ...form };

        if (formToSave?.notificationEmail?.length) {
            formToSave.notificationEmail = formToSave.notificationEmail.map((item) => ({
                label: item.label,
                type: item.type,
                value: item.value,
                email: item.subLabel || item.email || item.value,
            }));
        }

        if (!formToSave?.enabledErrorNotification) {
            formToSave.notificationEmail = [];
        }

        onSave(formToSave).then(() => setTimeout(() => saveCb?.current?.(formToSave)));
    }, [errors?.description, errors?.name, form, onSave]);

    const isConfirmDisabled =
        errors?.name ||
        errors?.description ||
        !form?.name ||
        (form?.enabledErrorNotification && !form?.notificationEmail?.length);

    return (
        <Modal
            overlayClassName="fm-automation-builder__header-form"
            isOpen={open}
            onRequestClose={handleCancel}
            onCancel={handleCancel}
            onConfirm={handleSave}
            confirmText={getLiteral('action_save')}
            cancelText={getLiteral('action_cancel')}
            title={getLiteral('label_automation_edit_name')}
            isConfirmDisabled={isConfirmDisabled}
        >
            <InformationSection form={form} onChange={handleFormChange} onErrorChange={setErrors} />
            <Spacer y={4} />
            <IntegrationsSection
                integrations={integrations}
                selectedIntegrations={form?.integrations || []}
                onChange={handleIntegrationsChange}
            />
        </Modal>
    );
});

export default AutomationBuilderHeaderModalForm;
